import React, { useState, useEffect, useMemo } from "react";
import { darkToast, toast, ToastContainer } from "../../Components/Toast";
import ReactLoading from "react-loading";
import { useHistory } from "react-router";
import axios from "axios";
import { serverUrl } from "../../Utils/server";

const AddProduct = () => {
  const history = useHistory();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [supplierPrice, setSupplierPrice] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [promoPrice, setPromoPrice] = useState("");
  const [permiteCreditoFiscal, setPermiteCreditoFiscal] = useState(false);
  const [salePriceFiscal, setSalePriceFiscal] = useState("");
  const [promoPriceFiscal, setPromoPriceFiscal] = useState("");
  const [stock, setStock] = useState("");
  const [weight, setWeight] = useState("");
  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [tags, setTags] = useState("");

  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [categories, setCategories] = useState([]);
  const [catalogCategories, setCatalogCategories] = useState([]);

  const [legacyCategoryIndex, setLegacyCategoryIndex] = useState(-1);

  const [level1Id, setLevel1Id] = useState("");
  const [level2Id, setLevel2Id] = useState("");
  const [level3Id, setLevel3Id] = useState("");

  useEffect(() => {
    getCategories();
    getCatalogCategories();
  }, []);

  const getCategories = () => {
    const config = {
      method: "get",
      url: `${serverUrl}/categories/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
    };

    axios(config)
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : [];
        setCategories(rows);
        setLegacyCategoryIndex(rows.length > 0 ? 0 : -1);
      })
      .catch(() => {
        toast("Couldn't load legacy categories.", darkToast);
      });
  };

  const getCatalogCategories = () => {
    const config = {
      method: "get",
      url: `${serverUrl}/catalog-categories/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
    };

    axios(config)
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : [];
        setCatalogCategories(rows);
      })
      .catch(() => {
        toast("Couldn't load catalog categories.", darkToast);
      });
  };

  const level1Options = useMemo(() => {
    return catalogCategories
      .filter((item) => Number(item?.level || 0) === 1)
      .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "es", { sensitivity: "base" }));
  }, [catalogCategories]);

  const level2Options = useMemo(() => {
    if (!level1Id) return [];
    return catalogCategories
      .filter(
        (item) =>
          Number(item?.level || 0) === 2 &&
          String(item?.parentId || "") === String(level1Id)
      )
      .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "es", { sensitivity: "base" }));
  }, [catalogCategories, level1Id]);

  const level3Options = useMemo(() => {
    if (!level2Id) return [];
    return catalogCategories
      .filter(
        (item) =>
          Number(item?.level || 0) === 3 &&
          String(item?.parentId || "") === String(level2Id)
      )
      .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "es", { sensitivity: "base" }));
  }, [catalogCategories, level2Id]);

  const selectedLevel1 = useMemo(
    () => level1Options.find((item) => String(item._id) === String(level1Id)) || null,
    [level1Options, level1Id]
  );

  const selectedLevel2 = useMemo(
    () => level2Options.find((item) => String(item._id) === String(level2Id)) || null,
    [level2Options, level2Id]
  );

  const selectedLevel3 = useMemo(
    () => level3Options.find((item) => String(item._id) === String(level3Id)) || null,
    [level3Options, level3Id]
  );

  const selectedCatalogCategory = selectedLevel3 || selectedLevel2 || selectedLevel1 || null;

  useEffect(() => {
    if (!selectedCatalogCategory || categories.length === 0) return;

    const idx = categories.findIndex(
      (item) =>
        String(item?.name || "").trim().toLowerCase() ===
        String(selectedCatalogCategory?.name || "").trim().toLowerCase()
    );

    if (idx >= 0) {
      setLegacyCategoryIndex(idx);
    }
  }, [selectedCatalogCategory, categories]);

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    console.log("UPLOAD FILES SELECTED", files.map(f => ({
      name: f.name,
      size: f.size,
      type: f.type
    })));

    setImages(files);
    setUploadingImages(true);

    try {
      const uploadedUrls = [];

      for (const image of files) {
        const fileName = `${image.name}_${title}_${Date.now().toString()}`;
        console.log("UPLOAD START", fileName);

        const formData = new FormData();
        formData.append("image", image);

        const response = await axios.post(`${serverUrl}/upload/image`, formData, {
          headers: {
            Authorization: `JWT ${localStorage.getItem("token")}`,
          },
          onUploadProgress: (event) => {
            const progress = event.total
              ? Math.round((event.loaded / event.total) * 100)
              : 0;
            console.log("UPLOAD PROGRESS", fileName, progress + "%");
          },
          timeout: 45000,
        });

        const url = response?.data?.url;
        if (!url) {
          throw new Error("Upload backend did not return url");
        }

        console.log("DOWNLOAD URL OK", fileName, url);
        uploadedUrls.push(url);
      }

      console.log("ALL UPLOADED URLS", uploadedUrls);
      setImageUrls(uploadedUrls);
      toast("Images uploaded successfully", darkToast);
    } catch (error) {
      console.error("UPLOAD IMAGES ERROR", error);
      toast(error?.message || "Error uploading images", darkToast);
    } finally {
      setUploadingImages(false);
    }
  };

  const upload = () => {
    if (!title.trim()) return toast("Title is required", darkToast);
    if (!description.trim()) return toast("Description is required", darkToast);
    if (!weight.toString().trim()) return toast("Weight is required", darkToast);
    if (salePrice === "" || Number(salePrice) <= 0) return toast("Sale Price is required", darkToast);
    if (promoPrice !== "" && Number(promoPrice) < 0) return toast("Promo Price cannot be negative", darkToast);
    if (supplierPrice !== "" && Number(supplierPrice) < 0) return toast("Supplier Price cannot be negative", darkToast);
    if (permiteCreditoFiscal && (salePriceFiscal === "" || Number(salePriceFiscal) <= 0)) return toast("Sale Price Fiscal is required", darkToast);
    if (promoPriceFiscal !== "" && Number(promoPriceFiscal) < 0) return toast("Promo Price Fiscal cannot be negative", darkToast);
    if (stock === "" || Number(stock) < 0) return toast("Stock is required", darkToast);
    if (imageUrls.length === 0) return toast("Upload at least 1 image", darkToast);
    if (!selectedCatalogCategory) return toast("Select a catalog category", darkToast);

    const fallbackLegacyCategory =
      legacyCategoryIndex >= 0 && categories[legacyCategoryIndex]
        ? categories[legacyCategoryIndex]
        : {
            _id: selectedCatalogCategory._id,
            name: selectedCatalogCategory.name,
          };

    setLoading(true);

    const config = {
      method: "post",
      url: `${serverUrl}/products/add`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
      data: {
        title: title.trim(),
        description: description.trim(),
        currencyCode: currencyCode,
        supplierPrice: supplierPrice === "" ? null : Number(supplierPrice),
        salePrice: Number(salePrice),
        promoPrice: promoPrice === "" ? null : Number(promoPrice),
        permiteCreditoFiscal: permiteCreditoFiscal,
        salePriceFiscal: salePriceFiscal === "" ? null : Number(salePriceFiscal),
        promoPriceFiscal: promoPriceFiscal === "" ? null : Number(promoPriceFiscal),
        stock: Number(stock),
        weight: weight.toString().trim(),
        images: imageUrls,
        category: {
          id: fallbackLegacyCategory._id,
          name: fallbackLegacyCategory.name,
        },
        catalogCategoryId: selectedCatalogCategory._id,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      },
    };

    console.log("ADD PRODUCT PAYLOAD", config.data);

    axios(config)
      .then((response) => {
        console.log("ADD PRODUCT RESPONSE", response.data);

        if (response.status === 200) {
          toast("Product added!", darkToast);

          setTimeout(() => {
            history.push("/products");
            window.location.reload();
          }, 1000);
        }
      })
      .catch((err) => {
        console.error("ADD PRODUCT ERROR", err);
        console.error("ADD PRODUCT ERROR RESPONSE", err?.response?.data);

        const msg =
          err?.response?.data?.msg ||
          err?.response?.data ||
          err?.message ||
          "Something went wrong!";

        toast(String(msg), darkToast);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="content-body">
      <div className="content-title">
        <p>Add New Product</p>
      </div>

      <div className="form">
        <p>Product Title</p>
        <input
          type="text"
          placeholder="Product Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <p>Description</p>
        <input
          type="text"
          value={description}
          placeholder="Description"
          onChange={(e) => setDescription(e.target.value)}
        />

        <p>Permite Crédito Fiscal</p>
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
          <input
            type="checkbox"
            checked={permiteCreditoFiscal}
            onChange={(e) => setPermiteCreditoFiscal(e.target.checked)}
          />
          Sí, este producto maneja precio fiscal
        </label>

        <p>Sale Price Fiscal</p>
        <input
          type="number"
          placeholder="Sale Price Fiscal"
          value={salePriceFiscal}
          disabled={!permiteCreditoFiscal}
          onChange={(e) => setSalePriceFiscal(e.target.value)}
        />

        <p>Promo Price Fiscal</p>
        <input
          type="number"
          placeholder="Promo Price Fiscal"
          value={promoPriceFiscal}
          disabled={!permiteCreditoFiscal}
          onChange={(e) => setPromoPriceFiscal(e.target.value)}
        />

        <p>Stock</p>
        <input
          type="number"
          placeholder="Stock"
          value={stock}
          onChange={(e) => setStock(e.target.value)}
        />

        <p>Weight</p>
        <input
          type="text"
          placeholder="Weight"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
        />

        <p>Images</p>
        {uploadingImages ? (
          <div style={{ margin: "1rem" }}>
            <ReactLoading
              type={"spinningBubbles"}
              color={"var(--primaryColor)"}
              width={"2rem"}
              height={"2rem"}
            />
          </div>
        ) : (
          <>
            {imageUrls.length === 0 ? (
              <input
                type="file"
                multiple
                onChange={uploadImages}
              />
            ) : null}

            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {imageUrls.map((url, idx) => (
                <img
                  key={idx}
                  style={{
                    width: "20%",
                    margin: "1rem",
                    objectFit: "contain",
                  }}
                  src={url}
                  alt=""
                />
              ))}
            </div>
          </>
        )}

        <p>Catalog Category - Level 1</p>
        <select
          value={level1Id}
          onChange={(e) => {
            setLevel1Id(e.target.value);
            setLevel2Id("");
            setLevel3Id("");
          }}
        >
          <option value="">Select level 1</option>
          {level1Options.map((item) => (
            <option key={item._id} value={item._id}>
              {item.name}
            </option>
          ))}
        </select>

        <p>Catalog Category - Level 2</p>
        <select
          value={level2Id}
          onChange={(e) => {
            setLevel2Id(e.target.value);
            setLevel3Id("");
          }}
          disabled={!level1Id || level2Options.length === 0}
        >
          <option value="">Select level 2</option>
          {level2Options.map((item) => (
            <option key={item._id} value={item._id}>
              {item.name}
            </option>
          ))}
        </select>

        <p>Catalog Category - Level 3</p>
        <select
          value={level3Id}
          onChange={(e) => setLevel3Id(e.target.value)}
          disabled={!level2Id || level3Options.length === 0}
        >
          <option value="">Select level 3</option>
          {level3Options.map((item) => (
            <option key={item._id} value={item._id}>
              {item.name}
            </option>
          ))}
        </select>

        <p>Selected Catalog Category</p>
        <input
          type="text"
          value={selectedCatalogCategory ? selectedCatalogCategory.name : ""}
          placeholder="No catalog category selected"
          disabled
        />

        <p>Legacy Category (auto / fallback)</p>
        <select
          value={legacyCategoryIndex}
          onChange={(e) => setLegacyCategoryIndex(parseInt(e.target.value, 10))}
        >
          {categories.length === 0 ? (
            <option value={-1}>No legacy categories</option>
          ) : (
            categories.map((categoryItem, index) => (
              <option key={categoryItem._id} value={index}>
                {categoryItem.name}
              </option>
            ))
          )}
        </select>

        <p>Tags</p>
        <input
          type="text"
          value={tags}
          placeholder="Tags separated by comma"
          onChange={(e) => setTags(e.target.value)}
        />

        <button
          className={loading || uploadingImages ? "disabled" : ""}
          onClick={upload}
        >
          {loading ? (
            <ReactLoading
              type={"spinningBubbles"}
              color={"white"}
              width={"1.2rem"}
              height={"1.2rem"}
            />
          ) : (
            "Add Product"
          )}
        </button>
      </div>

      <ToastContainer />
    </div>
  );
};

export default AddProduct;
